import config from '../config';

class PasswordService {
  constructor() {
    this.endpoint = `${config.API_URL}${config.components.password.endpoint}`;
    this.storageKey = 'app_password'; // Chiave predefinita
    this.maxAttempts = config.components.password.maxAttempts;
    this.lockoutDuration = config.components.password.lockoutDuration;
    this.attempts = 0;
    this.lockoutUntil = null;
    this.translationFunction = null; // Funzione di traduzione
    this.currentLanguage = localStorage.getItem('language') || 'it';
  }

  // Metodo per impostare una chiave di storage personalizzata
  setStorageKey(key) {
    this.storageKey = key;
  }

  // Registra la funzione di traduzione
  setTranslation(translationFunction) {
    this.translationFunction = translationFunction;
  }

  // Imposta la lingua corrente
  setLanguage(language) {
    this.currentLanguage = language;
  }

  // Traduce un messaggio di errore se necessario
  translateErrorMessage(errorMessage) {
    // Se la lingua è italiana o la traduzione non è configurata, restituisci il messaggio originale
    if (this.currentLanguage === 'it' || !this.translationFunction) {
      return errorMessage;
    }

    // Mappa dei messaggi di errore in italiano con le loro traduzioni inglesi
    const errorTranslations = {
      'Il server non è disponibile al momento. Contatta l\'amministratore.': 'serverNonDisponibile',
      'Errore di comunicazione con il server. Riprova più tardi.': 'erroreServer',
      'Password disattivata': 'passwordDisattivata',
      'Password scaduta': 'passwordScaduta',
      'Password già utilizzata': 'passwordGiaUtilizzata',
      'Password non trovata': 'passwordNonTrovata',
      'Password non valida. Riprova.': 'passwordNonValida',
      'La password non è più valida. Inserisci una nuova password.': 'passwordNonPiuValida',
      'Errore durante la verifica della password': 'erroreVerificaPassword'
    };

    // Cerca una corrispondenza esatta
    if (errorTranslations[errorMessage]) {
      return this.translationFunction(errorTranslations[errorMessage]);
    }

    // Se non c'è una corrispondenza esatta, cerca una corrispondenza parziale
    for (const [italianError, translationKey] of Object.entries(errorTranslations)) {
      if (errorMessage.includes(italianError)) {
        return this.translationFunction(translationKey);
      }
    }

    // Se non è stata trovata alcuna corrispondenza, restituisci il messaggio originale
    return errorMessage;
  }

  // Verifica se la password è già memorizzata
  hasStoredPassword() {
    return !!localStorage.getItem(this.storageKey);
  }

  // Recupera la password memorizzata
  getStoredPassword() {
    return localStorage.getItem(this.storageKey);
  }

  // Salva la password nel localStorage
  savePassword(password) {
    localStorage.setItem(this.storageKey, password);
  }

  // Rimuove la password dal localStorage
  clearPassword() {
    localStorage.removeItem(this.storageKey);
    this.attempts = 0;
    this.lockoutUntil = null;
  }

  // Verifica la password con l'API
  async verifyPassword(password = null) {
    // Se siamo in lockout, verifica se è scaduto
    if (this.lockoutUntil && Date.now() < this.lockoutUntil) {
      const remainingTime = Math.ceil((this.lockoutUntil - Date.now()) / 1000);
      throw new Error(`Troppi tentativi. Riprova tra ${remainingTime} secondi.`);
    }

    // Se non viene fornita una password, prova a recuperarla dallo storage
    const isStoredPassword = !password;
    if (!password) {
      password = this.getStoredPassword();
      if (!password) {
        throw new Error('Password non trovata');
      }
    }

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      // Se è un successo (prima attivazione), salva la password e resetta i tentativi
      if (data.success) {
        this.attempts = 0;
        this.lockoutUntil = null;
        this.savePassword(password);
        // Salva anche i dati di attivazione
        if (data.attivata_il && data.scade_il) {
          localStorage.setItem('password_activation', JSON.stringify({
            attivata_il: data.attivata_il,
            scade_il: data.scade_il
          }));
        }
        return true;
      }

      // Gestione degli errori specifici
      if (data.error) {
        // Se la password è nel localStorage e riceviamo "Password già utilizzata",
        // significa che è una password valida che stiamo già usando
        if (isStoredPassword && data.error === 'Password già utilizzata') {
          this.attempts = 0;
          this.lockoutUntil = null;
          return true;
        }

        // Se stiamo tentando di attivare una nuova password e riceviamo "Password già utilizzata",
        // significa che qualcun altro l'ha già attivata
        if (!isStoredPassword && data.error === 'Password già utilizzata') {
          throw new Error('Questa password è già stata attivata da un altro utente');
        }

        this.attempts++;

        if (this.attempts >= this.maxAttempts) {
          this.lockoutUntil = Date.now() + this.lockoutDuration;
          throw new Error('Troppi tentativi falliti. Account bloccato temporaneamente.');
        }

        // Per gli altri errori (password scaduta, disattivata, non trovata), 
        // lancia l'errore specifico
        throw new Error(data.error);
      }

      throw new Error('Errore durante la verifica della password');
    } catch (error) {
      if (!error.message.includes('Troppi tentativi')) {
        console.error('Errore durante la verifica della password:', error);
      }
      throw error;
    }
  }

  // Verifica lo stato della password
  async checkPasswordStatus() {
    if (!this.hasStoredPassword()) {
      return false;
    }

    try {
      return await this.verifyPassword();
    } catch (error) {
      // Se l'errore è dovuto a troppi tentativi o lockout, non cancellare la password
      if (error.message.includes('Troppi tentativi')) {
        return false;
      }
      
      // Se l'errore è dovuto a password disattivata o scaduta, cancella la password
      if (error.message === 'Password disattivata' || error.message === 'Password scaduta') {
        this.clearPassword();
      }
      
      // Per altri errori, non cancellare la password ma restituisci false
      return false;
    }
  }

  // Nuovo metodo per ottenere i dati di attivazione
  getActivationData() {
    const data = localStorage.getItem('password_activation');
    return data ? JSON.parse(data) : null;
  }
}

export const passwordService = new PasswordService(); 